// Role-based permission system
export interface UserPermissions {
  canViewDashboard: boolean;
  canViewMembers: boolean;
  canManageMembers: boolean;
  canViewClasses: boolean;
  canManageClasses: boolean;
  canViewWorkouts: boolean;
  canManageWorkouts: boolean;
  canViewEmployees: boolean;
  canManageEmployees: boolean;
  canViewCommunication: boolean;
  canManageCommunication: boolean;
  canViewBilling: boolean;
  canManageBilling: boolean;
  canViewReports: boolean;
  canManageReports: boolean;
  canViewHandbook: boolean;
  canManageHandbook: boolean;
  canViewGymSettings: boolean;
  canManageGymSettings: boolean;
}

export type UserRole = 'owner' | 'employee' | 'viewer';

// Define permissions for each role
const rolePermissions: Record<UserRole, UserPermissions> = {
  owner: {
    canViewDashboard: true,
    canViewMembers: true,
    canManageMembers: true,
    canViewClasses: true,
    canManageClasses: true,
    canViewWorkouts: true,
    canManageWorkouts: true,
    canViewEmployees: true,
    canManageEmployees: true,
    canViewCommunication: true,
    canManageCommunication: true,
    canViewBilling: true,
    canManageBilling: true,
    canViewReports: true,
    canManageReports: true,
    canViewHandbook: true,
    canManageHandbook: true,
    canViewGymSettings: true,
    canManageGymSettings: true,
  },
  employee: {
    canViewDashboard: true,
    canViewMembers: true,
    canManageMembers: true,
    canViewClasses: true,
    canManageClasses: true,
    canViewWorkouts: true,
    canManageWorkouts: true,
    canViewEmployees: true,
    canManageEmployees: true,
    canViewCommunication: true,
    canManageCommunication: true,
    canViewBilling: false, // No billing access
    canManageBilling: false,
    canViewReports: true,
    canManageReports: true,
    canViewHandbook: true,
    canManageHandbook: true,
    canViewGymSettings: true,
    canManageGymSettings: false, // Can view but not manage gym settings
  },
  viewer: {
    canViewDashboard: true,
    canViewMembers: true,
    canManageMembers: false, // View only
    canViewClasses: true,
    canManageClasses: false, // View only
    canViewWorkouts: true,
    canManageWorkouts: false, // View only
    canViewEmployees: true,
    canManageEmployees: false, // View only
    canViewCommunication: true,
    canManageCommunication: false, // View only
    canViewBilling: false, // No billing access
    canManageBilling: false,
    canViewReports: true,
    canManageReports: false, // View only
    canViewHandbook: true,
    canManageHandbook: false, // View only
    canViewGymSettings: true,
    canManageGymSettings: false, // View only
  },
};

// Get permissions for a specific role
export function getPermissionsForRole(role: UserRole): UserPermissions {
  return rolePermissions[role] || rolePermissions.viewer;
}

// Check if user has specific permission
export function hasPermission(userRole: UserRole, permission: keyof UserPermissions): boolean {
  const permissions = getPermissionsForRole(userRole);
  return permissions[permission];
}

// Get role display name
export function getRoleDisplayName(role: UserRole): string {
  const roleMap: Record<UserRole, string> = {
    owner: 'Owner',
    employee: 'Employee',
    viewer: 'Viewer',
  };
  return roleMap[role] || 'Unknown';
}

// Get role description
export function getRoleDescription(role: UserRole): string {
  const descriptions: Record<UserRole, string> = {
    owner: 'Full access to all features including billing & payments',
    employee: 'Access to all features except billing & payments',
    viewer: 'View-only access to all pages except billing & payments',
  };
  return descriptions[role] || 'Unknown role';
}

// Get role color for UI
export function getRoleColor(role: UserRole): string {
  const colors: Record<UserRole, string> = {
    owner: 'text-purple-400',
    employee: 'text-blue-400',
    viewer: 'text-green-400',
  };
  return colors[role] || 'text-gray-400';
}

// Check if user can access a specific page
export function canAccessPage(userRole: UserRole, page: string): boolean {
  const permissions = getPermissionsForRole(userRole);
  
  switch (page) {
    case 'dashboard':
      return permissions.canViewDashboard;
    case 'members':
      return permissions.canViewMembers;
    case 'classes':
      return permissions.canViewClasses;
    case 'workouts':
      return permissions.canViewWorkouts;
    case 'employee-management':
      return permissions.canViewEmployees;
    case 'communication':
      return permissions.canViewCommunication;
    case 'reports':
      return permissions.canViewReports;
    case 'handbook':
      return permissions.canViewHandbook;
    case 'billing':
      return permissions.canViewBilling;
    case 'gym-settings':
      return permissions.canViewGymSettings;
    default:
      return false;
  }
}

// Check if user can perform a specific action
export function canPerformAction(userRole: UserRole, action: string): boolean {
  const permissions = getPermissionsForRole(userRole);
  
  switch (action) {
    case 'create_member':
    case 'edit_member':
    case 'delete_member':
      return permissions.canManageMembers;
    case 'create_class':
    case 'edit_class':
    case 'delete_class':
      return permissions.canManageClasses;
    case 'create_workout':
    case 'edit_workout':
    case 'delete_workout':
      return permissions.canManageWorkouts;
    case 'create_employee':
    case 'edit_employee':
    case 'delete_employee':
      return permissions.canManageEmployees;
    case 'view_billing':
    case 'manage_billing':
      return permissions.canViewBilling;
    case 'manage_gym_settings':
      return permissions.canManageGymSettings;
    default:
      return false;
  }
}
